import { Component, inject, Input } from '@angular/core';
import { ToastService } from '@cnt-workspace/data-access';
import { IonButton } from '@ionic/angular/standalone';
import { CsvColumn, csvFilename, downloadCsv, toCsv } from './csv';

/**
 * "Export CSV" for a list: the rows on hand, or `fetch` for lists the server
 * pages (it should return every row matching the current filters). Columns
 * are the page's own, so the file reads like the table.
 */
@Component({
  selector: 'admin-export-button',
  standalone: true,
  imports: [IonButton],
  template: `
    <ion-button fill="outline" size="small" [disabled]="busy || (!fetch && !rows?.length)" (click)="export()">
      <span class="material-symbols-outlined" aria-hidden="true" slot="start">download</span>
      {{ busy ? 'Preparing…' : label }}
    </ion-button>
  `,
  styles: [
    `
      .material-symbols-outlined {
        margin-right: 4px;
        font-size: 18px;
      }
    `,
  ],
})
export class AdminExportButtonComponent<T = unknown> {
  private readonly toasts = inject(ToastService);

  /** File name stem: "reservations" → reservations-2026-10-21.csv. */
  @Input({ required: true }) name = '';
  @Input({ required: true }) columns: readonly CsvColumn<T>[] = [];
  /** The rows to export when they're all on the page. */
  @Input() rows?: readonly T[] | null;
  /** Loads every row (for server-paged lists); used instead of `rows`. */
  @Input() fetch?: () => Promise<readonly T[]>;
  @Input() label = 'Export CSV';

  busy = false;

  async export(): Promise<void> {
    this.busy = true;
    try {
      const rows = this.fetch ? await this.fetch() : this.rows ?? [];
      downloadCsv(csvFilename(this.name), toCsv(rows, this.columns));
      this.toasts.success(`Exported ${rows.length} row${rows.length === 1 ? '' : 's'}`);
    } catch {
      this.toasts.error("Couldn't prepare the export. Try again.");
    } finally {
      this.busy = false;
    }
  }
}
